<template>
  <div class="page-wrapper">
    <v-card v-if="$vuetify.breakpoint.lgAndUp" class="ratings-card-lg" max-width="400">
      <h2>Ratings</h2>
      <work-page-ratings-section
        :ratings="statistics.ratings"
        :rating-circle-size="100"
        :vertical="true"
      ></work-page-ratings-section>
      <div v-if="$vuetify.breakpoint.mdAndUp" class="d-flex justify-space-around flex-column">
        <!-- Friends who have engaged this work-->
        <template v-if="isLoggedIn">
          <v-divider></v-divider>
          <work-page-friend-engagements :medium="medium" :work-id="workId"></work-page-friend-engagements>
        </template>
      </div>
    </v-card>

    <div class="main-col d-flex justify-start align-center flex-column">
      <v-btn
        v-if="!$vuetify.breakpoint.mdAndUp && isLoggedIn"
        @click="navigateToNewReview"
        color="primary"
        class="mb-4"
      >
        New Review
      </v-btn>
      <v-card width="100%" class="work-details-card">
        <div v-if="!$vuetify.breakpoint.mdAndUp" :style="{ height: '200px' }">
          <blurred-margin-poster :src="workDetails.posterSource">
            <medium-icon :medium="medium" />
            <div v-if="isLoggedIn" class="overlay-actions-container">
              <v-btn v-bind="overlayActionCommonProps" @click="toggleEngaged">
                <v-icon v-if="userWorkRelationships.engaged" color="primary lighten-1">mdi-eye-check</v-icon>
                <v-icon v-else>mdi-eye</v-icon>
              </v-btn>
              <v-btn v-bind="overlayActionCommonProps" @click="toggleSavedForLater">
                <v-icon v-if="userWorkRelationships.savedForLater" color="primary lighten-1">mdi-bookmark-check</v-icon>
                <v-icon v-else>mdi-bookmark</v-icon>
              </v-btn>
            </div>
          </blurred-margin-poster>
        </div>

        <v-img
          v-else
          :src="workDetails.posterSource"
          class="poster-standalone align-self-start"
          width="33%"
          max-width="33%"
        ></v-img>

        <div class="pa-2 pa-md-0">
          <h1>{{ workDetails.title }}</h1>
          <p class="info-line" v-if="workDetails.creators.length > 0">
            <span class="info-label">{{ mediumSpecificWordForCreator(medium, workDetails.creators.length > 1) }} </span
            >{{ workDetails.creators.map((creator) => creator.name).join(", ") }}</p
          >
          <p class="info-line"><span class="info-label">Released</span> {{ formattedReleaseDate }}</p>
          <!-- <p class="info-line"><span class="info-label">Seasons</span> {{ seasonCount }}</p> -->
          <template v-if="$vuetify.breakpoint.mdAndUp">
            <div v-if="workDetails.description" v-html="workDetails.description" class="pt-2 work-description"></div>
            <!-- todo: Factor entries list into a component -->
            <template v-if="workDetails.entries">
              <div class="mt-2 info-label font-weight-bold">
                {{ mediumSpecificWordForEntry(medium, true) }}
              </div>
              <ol class="work-entry-list">
                <li v-for="(entry, index) in workDetails.entries" :key="index">
                  {{ entry.title }}
                </li>
              </ol>
            </template>
          </template>
        </div>

        <template v-if="!$vuetify.breakpoint.mdAndUp">
          <v-divider class="mx-2"></v-divider>

          <work-page-ratings-section
            :ratings="statistics.ratings"
            :rating-circle-size="64"
            justify="space-around"
          ></work-page-ratings-section>
          <template v-if="isLoggedIn && workDetails.id">
            <v-divider class="mx-2"></v-divider>
            <div class="px-2 pb-2">
              <work-page-friend-engagements :medium="medium" :work-id="workId"></work-page-friend-engagements>
            </div>
          </template>
        </template>
      </v-card>

      <v-card class="mt-4 info-reviews-tab">
        <v-tabs
          v-if="!$vuetify.breakpoint.mdAndUp"
          v-model="activeTab"
          class="work-info-tabs"
          fixed-tabs
          height="28px"
          slider-size="6"
          slider-color="text lighten-2"
          background-color="card"
        >
          <v-tab tab-value="info">Info</v-tab>
          <v-tab tab-value="reviews">Reviews</v-tab>
        </v-tabs>
        <h2 v-else>Reviews</h2>
        <v-tabs-items v-model="activeTabOrReviews">
          <v-tab-item value="info" class="pa-2">
            <div v-if="workDetails.description" v-html="workDetails.description" class="ma-0 work-description"></div>
            <template v-if="workDetails.entries">
              <div class="info-label font-weight-bold">
                {{ mediumSpecificWordForEntry(medium, true) }}
              </div>
              <ol class="work-entry-list">
                <li v-for="(entry, index) in workDetails.entries" :key="index">
                  {{ entry.title }}
                </li>
              </ol>
            </template>
          </v-tab-item>

          <!-- REVIEWS -->
          <v-tab-item value="reviews" class="pa-2">
            <template v-if="isLoggedIn">
              <chip-radio v-model="activeReviewSet" :options="Object.values(AUTHENTICATED_RATING_SETS)"></chip-radio>
              <v-divider class="mt-2"></v-divider>
            </template>

            <div>
              <div v-if="reviews[activeReviewSet].items.length === 0">
                <p class="no-items-found-message no-reviews-found">no reviews found</p>
              </div>
              <review-row-work-page
                v-else
                v-for="review in reviews[activeReviewSet].items"
                :key="review.id"
                class="review"
                :review="review"
              />
              <template v-if="reviews[activeReviewSet].hasMoreItems">
                <button @click="loadMoreReviews(activeReviewSet)" class="button-text-gray-underlined load-more">
                  Load More
                </button>
              </template>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>

    <!-- TODO: Move higher in the DOM, but keep position-->
    <v-card class="work-options-tile ml-4" v-if="$vuetify.breakpoint.mdAndUp" max-width="400px">
      <v-list
        :class="{
          'pb-0': $vuetify.breakpoint.md,
        }"
      >
        <p v-if="!isLoggedIn" class="logged-out-hint">Log in to interact with this work</p>
        <div class="d-flex justify-space-around">
          <button @click="navigateToNewReview" class="button-work-action-card" :disabled="!isLoggedIn">
            <v-icon size="36">mdi-file-document-edit</v-icon><br />
            Write a Review
          </button>

          <button @click="toggleEngaged" class="button-work-action-card" :disabled="!isLoggedIn">
            <template v-if="userWorkRelationships.engaged">
              <v-icon color="primary">mdi-eye-check</v-icon><br />
              Previously {{ mediumSpecificVerbForEngaged(this.medium) }}
            </template>
            <template v-else>
              <v-icon>mdi-eye</v-icon><br />
              Mark as {{ mediumSpecificVerbForEngaged(this.medium) }}
            </template>
          </button>

          <button @click="toggleSavedForLater" class="button-work-action-card" :disabled="!isLoggedIn">
            <template v-if="userWorkRelationships.savedForLater">
              <v-icon color="primary">mdi-bookmark-check</v-icon><br />
              Saved for Later
            </template>
            <template v-else>
              <v-icon>mdi-bookmark</v-icon><br />
              Save for Later
            </template>
          </button>
        </div>
        <template v-if="shouldShowAdminTools">
          <v-divider></v-divider>
          <v-list-item> Admin Tools </v-list-item>
          <v-list-item class="admin-item-action-card" @click="refreshCache"> Refresh Cache </v-list-item>
        </template>
      </v-list>

      <template v-if="$vuetify.breakpoint.md">
        <v-divider class="mx-2"></v-divider>

        <work-page-ratings-section
          :ratings="statistics.ratings"
          :rating-circle-size="64"
          justify="space-around"
        ></work-page-ratings-section>
        <template v-if="isLoggedIn && workDetails.id">
          <v-divider class="mx-2"></v-divider>
          <div class="px-2 pb-2">
            <work-page-friend-engagements :medium="medium" :work-id="workId"></work-page-friend-engagements>
          </div>
        </template>
      </template>
    </v-card>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { mapMutations } from "vuex";
// Components
import BlurredMarginPoster from "../components/poster-blurred-margins.vue";
import ReviewRowWorkPage from "../components/work-page/work-page-review-row.vue";
import WorkPageFriendEngagements from "../components/work-page/work-page-friend-engagements.vue";
import WorkPageRatingsSection from "../components/work-page/work-page-ratings-section.vue";
import ChipRadio from "../components/chip-radio.vue";
// Mixins
import SessionTools from "@/mixins/sessionTools";
import WatchlistAndSeen from "@/mixins/watchlistAndSeen";
// Util
import isAdmin from "@/util/isAdmin";
import { formatDate } from "@/util/dateHelpers";
import { uriSafeTitle } from "../util/stringHelpers";
import { get404Route } from "@/util/routeHelpers";
import {
  mediumSpecificWordForCreator,
  mediumSpecificVerbForEngaged,
  mediumSpecificWordForEntry,
} from "@/util/mediumHelpers";

const AUTHENTICATED_RATING_SETS = {
  PUBLIC: "Public",
  FRIENDS: "Friends",
  YOU: "You",
};
const UNATHENTICATED_RATING_SETS = {
  ANY: "Any",
};
const RATING_SETS = Object.assign({}, AUTHENTICATED_RATING_SETS, UNATHENTICATED_RATING_SETS);

const REVIEW_LIMIT_PER_REQUEST = 5;

const ROUTE_PATH_REGEX = /^(\/\w+\/\d+)(-?)(.*)$/;

function ratingSetToFriendshipStatus(ratingSet) {
  switch (ratingSet) {
    case RATING_SETS.PUBLIC:
      return "public";
    case RATING_SETS.FRIENDS:
      return "friend";
    case RATING_SETS.ANY:
    case RATING_SETS.YOU:
      return "any";
    default:
      throw new Error("Invalid argument");
  }
}

export default defineComponent({
  name: "work-page.vue",
  components: {
    BlurredMarginPoster,
    ChipRadio,
    ReviewRowWorkPage,
    WorkPageRatingsSection,
    WorkPageFriendEngagements,
  },
  mixins: [SessionTools, WatchlistAndSeen],

  data: function () {
    return {
      AUTHENTICATED_RATING_SETS,

      activeTab: null,
      activeReviewSet: RATING_SETS.FRIENDS,

      workDetails: {
        id: undefined,
        medium: undefined,
        title: "Loading...",
        posterSource: undefined,
        description: "Loading...",
        creators: [],
        releaseDate: undefined,
      },

      reviews: {
        [RATING_SETS.PUBLIC]: {
          hasMoreItems: true,
          items: [],
        },
        [RATING_SETS.FRIENDS]: {
          hasMoreItems: true,
          items: [],
        },
        [RATING_SETS.YOU]: {
          hasMoreItems: true,
          items: [],
        },
        [RATING_SETS.ANY]: {
          hasMoreItems: true,
          items: [],
        },
      },
      revealedSpoilers: {
        // reviewId: isRevealed
      },

      statistics: {
        ratings: {
          [RATING_SETS.PUBLIC]: {
            score: undefined,
            count: undefined,
          },
          [RATING_SETS.FRIENDS]: {
            score: undefined,
            count: undefined,
          },
          [RATING_SETS.YOU]: {
            score: undefined,
            count: undefined,
          },
        },
      },

      userWorkRelationships: {
        engaged: false,
        savedForLater: false,
      },

      overlayActionCommonProps: {
        class: "button-work-action-overlay",
        fab: true,
        dark: true,
        color: "var(--semi-opaque-overlay-bg-color-darker)",
        elevation: "0",
        small: true,
      },

      userIsAdmin: false,
    };
  },

  computed: {
    availableReviewSets() {
      if (this.isLoggedIn) {
        return Object.values(AUTHENTICATED_RATING_SETS);
      } else {
        return Object.values(UNATHENTICATED_RATING_SETS);
      }
    },

    formattedReleaseDate() {
      // TODO: Handle date range (for tv shows and podcasts)
      // Example  "December 25, 2019 - Present Day";
      if (!this.workDetails.releaseDate) return null;
      return formatDate(this.workDetails.releaseDate);
    },

    seasonCount() {
      if (this.medium != this.$mediums.TV_SHOW) return null;
      return this.workDetails.entries.count;
    },

    activeTabOrReviews() {
      return this.$vuetify.breakpoint.mdAndUp ? "reviews" : this.activeTab;
    },

    shouldShowAdminTools() {
      return this.isLoggedIn && this.userIsAdmin;
    },

    userId() {
      return this.$store.state.userId;
    },

    workId() {
      return this.$route.params.workId;
    },

    medium() {
      // Use the route param instead of work details to allow for more concurrent loading of resources that need to know the medium
      return this.$route.params.medium;
    },
  },

  watch: {
    activeReviewSet() {
      console.log("UPDATING ACTIVE REVIEW SET");
      console.log(this.activeReviewSet);
    },
  },

  async mounted() {
    this.userIsAdmin = await isAdmin(this.userId);
    this.workDetails.medium = this.medium;
    this.workDetails.id = this.workId;
    this.activeReviewSet = this.isLoggedIn ? RATING_SETS.FRIENDS : RATING_SETS.ANY;

    try {
      const response = await this.$axios.get(`/v1/works/${this.medium}/${this.workId}`);
      this.workDetails = response.data;
      // Add title to route
      const regexMatch = this.$route.path.match(ROUTE_PATH_REGEX);
      const corePath = regexMatch.length > 1 ? regexMatch[1] : this.$route.path;
      history.replaceState({}, null, corePath + "-" + uriSafeTitle(this.workDetails.title).toLowerCase());
    } catch (e) {
      if (e.response && e.response.status === 404) {
        // Reroute
        await this.$router.replace(get404Route(this.$route));
      } else {
        console.error(e);
      }
    }

    if (this.isLoggedIn) {
      await this.loadUserWorkRelationships();
    }

    let loadReviewPromises = [];
    for (const ratingSet of this.availableReviewSets) {
      loadReviewPromises.push(this.loadMoreReviews(ratingSet));
    }
    await Promise.all(loadReviewPromises);

    await this.loadRatings();
  },

  methods: {
    isAdmin,
    ...mapMutations(["showSnackbar"]),
    mediumSpecificVerbForEngaged,
    mediumSpecificWordForEntry,
    mediumSpecificWordForCreator,
    formatDate,

    async loadRatings() {
      const response = await this.$axios.get(`/v1/works/${this.medium}/${this.workId}/statistics`);
      console.log(response.data);
      this.statistics.ratings[AUTHENTICATED_RATING_SETS.PUBLIC].score = response.data.ratings.overall;
      this.statistics.ratings[AUTHENTICATED_RATING_SETS.FRIENDS].score = response.data.ratings.friends;
      this.statistics.ratings[AUTHENTICATED_RATING_SETS.YOU].score = response.data.ratings.you;
    },

    async loadMoreReviews(ratingSet) {
      if (!this.reviews[ratingSet].hasMoreItems) {
        console.info("No more reviews to retrieve for this ratingSet: " + ratingSet);
        return;
      }
      let params = {
        work: {
          id: this.workDetails.id,
          medium: this.workDetails.medium,
        },
        friendship: ratingSetToFriendshipStatus(ratingSet),
        offset: this.reviews[ratingSet].items.length,
        limit: REVIEW_LIMIT_PER_REQUEST,
      };
      if (ratingSet == AUTHENTICATED_RATING_SETS.YOU) {
        params.author = this.$store.state.userId;
      }
      const response = await this.$axios.get("/v1/reviews", {
        params,
      });
      this.reviews[ratingSet].items = this.reviews[ratingSet].items.concat(response.data.items);
      this.reviews[ratingSet].hasMoreItems = response.data.items.length >= REVIEW_LIMIT_PER_REQUEST;
    },

    async loadUserWorkRelationships() {
      const savedForLater = await this.$axios.get("/is_saved", {
        params: {
          medium: this.workDetails.medium,
          internal_id: this.workDetails.id,
        },
      });
      const engaged = await this.$axios.get("/is_seen", {
        params: {
          internal_id: this.workDetails.id,
          medium: this.workDetails.medium,
        },
      });
      this.userWorkRelationships.savedForLater = savedForLater.data;
      this.userWorkRelationships.engaged = engaged.data;
    },

    async toggleEngaged() {
      const willBeEngaged = !this.userWorkRelationships.engaged;
      try {
        if (willBeEngaged) {
          await this.markAsViewed(this.workDetails.id, this.workDetails.medium);
        } else {
          await this.deleteFromViewed(this.workDetails.id, this.workDetails.medium);
        }
        this.userWorkRelationships.engaged = willBeEngaged;
      } catch (err) {
        this.showSnackbar({
          text: "An error has occurred",
          error: true,
        });
      }
    },

    async toggleSavedForLater() {
      const willBeSavedForLater = !this.userWorkRelationships.savedForLater;
      try {
        if (willBeSavedForLater) {
          await this.saveForLater(this.workDetails.id, this.workDetails.medium);
        } else {
          await this.removeFromWatchlist(this.workDetails.id, this.workDetails.medium, false);
        }

        this.userWorkRelationships.savedForLater = willBeSavedForLater;
      } catch (err) {
        this.showSnackbar({
          text: "An unexpected error occurred",
          error: true,
        });
      }
    },

    async navigateToNewReview() {
      await this.$router.push({
        name: "rate",
        query: {
          medium: this.workDetails.medium,
          workID: this.workDetails.id,
        },
      });
    },

    async refreshCache() {
      try {
        await this.$axios.post(`/v1/works/${this.workDetails.medium}/${this.workDetails.id}/cache-invalidation`);
        this.showSnackbar({
          text: "Cache successfully refreshed",
        });
      } catch (err) {
        this.showSnackbar({
          text: "An error occurred",
          error: true,
        });
        return;
      }
      // Update work details
      const response = await this.$axios.get(`/v1/works/${this.medium}/${this.workId}`);
      this.workDetails = response.data;
    },
  },
});
</script>

<style scoped>
.page-wrapper {
  display: flex;
  flex-flow: row;
  justify-content: center;
  max-width: 1440px;
  margin: auto;
}

.ratings-card-lg {
  align-self: start;
  flex-grow: 1;
  margin-right: 16px;
  padding: 8px;
}

.main-col {
  max-width: 600px;
  width: 100%;
}

.logged-out-hint {
  text-align: center;
  font-weight: bold;
  color: var(--v-text-base);
}

.work-options-tile {
  align-self: start;
  flex-grow: 1;
}
.button-work-action-card {
  max-width: 78px;
  font-size: 14px;
  color: var(--v-text-base);
  padding: 4px;
}
.button-work-action-card:hover {
  text-decoration: underline;
}
.theme--dark {
  .button-work-action-card:hover:enabled {
    background-color: var(--v-card-lighten1);
  }
  .button-work-action-card:active:enabled {
    background-color: var(--v-card-lighten2);
  }
}
.theme--light {
  .button-work-action-card:hover:enabled {
    background-color: var(--v-card-darken1);
  }
  .button-work-action-card:active:enabled {
    background-color: var(--v-card-darken2);
  }
}
.button-work-action-card .v-icon {
  font-size: 36px;
}
.button-work-action-card:disabled,
.button-work-action-card:disabled .v-icon {
  text-decoration: none;
  color: var(--v-disabled-base);
  background-color: inherit;
}

.admin-item-action-card {
  padding-left: 50px;
}

.medium-icon {
  margin: 8px;
}

.overlay-actions-container {
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
}
.overlay-actions-container .button-work-action-overlay {
  margin: 0 8px 8px 0;
}

h1 {
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 6px;
}

h2 {
  font-size: 20px;
  color: var(--v-text-lighten1);
  font-weight: normal;
  margin-left: 6px;
}

.info-line {
  white-space: pre-wrap;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 0;
  line-height: 1.1;
}

.info-line > .info-label {
  font-size: 10px;
  font-weight: normal;
}

/* Tabs */
.info-reviews-tab {
  width: 100%;
}
.info-reviews-tab .no-ratings-found {
  margin-bottom: 0;
}

.v-tab {
  text-transform: none !important;
  color: var(--v-text-lighten1) !important;
  font-size: 16px !important;
  font-weight: normal !important;
}

.work-description {
  font-size: 16px;
  line-height: 1.3;
}
.work-description >>> p {
  margin: 8px 0 0 0;
}
.work-description >>> p:first-child {
  margin: 0;
}
.work-entry-list {
  line-height: 1.3;
}

#reviews,
#info {
  background-color: var(--v-card-base);
}

.load-more {
  line-height: 1;
  padding-top: 6px;
  display: block;
  position: relative;
  margin: auto;
}

.no-reviews-found {
  margin-top: 8px;
  margin-bottom: 0;
}

@media (min-width: 960px) {
  .work-details-card {
    display: flex;
    padding: 8px;
  }
  .work-details-card .poster-standalone {
    margin-right: 8px;
    border-radius: 5px;
  }
  h1 {
    font-size: 24px;
  }
  .info-line {
    font-size: 16px;
  }
  .info-line > .info-label {
    font-size: 12px;
  }
  /* TODO: Edit font size of other things */
}
</style>
